import React, { useEffect, useMemo, useRef, useState } from 'react';

import { formatDate } from '../../../../common-utils/formatters';
import type { Company } from '../../../@types/Company';
import type { FixedNewsletter, NewsletterType } from '../../../@types/Newsletter';
import { NEWSLETTER_TYPE_LABELS } from '../../../@types/Newsletter';
import { JWTHeader } from '../../../utils/JWTHeader';
import Popup from '../../common/Popup/Popup.module';
import MiniNewsletterPreview from '../../Communication/NewsletterList/components/MiniNewsletterPreview';

interface NewsletterPreviewPopupProps {
  isOpen: boolean;
  onClose: () => void;
  newsletterType: NewsletterType | null;
  themeLabel: string;
  jwt: string | null;
  company: Company | null;
  onOpenNewsletter: (newsletter: FixedNewsletter, templateName: string) => void;
  onOpenList: () => void;
}

type NewsletterCache = Partial<Record<NewsletterType, FixedNewsletter[]>>;

function getPreferredTemplate(newsletter: FixedNewsletter, company: Company | null): string | null {
  if (!newsletter.templates || newsletter.templates.length === 0) {
    return null;
  }

  const preferredTemplate = company?.brandingSettings?.defaultTemplate;
  if (preferredTemplate && newsletter.templates.some((template) => template.name === preferredTemplate)) {
    return preferredTemplate;
  }

  return newsletter.templates[0]?.name ?? null;
}

function NewsletterPreviewPopup({
  isOpen,
  onClose,
  newsletterType,
  themeLabel,
  jwt,
  company,
  onOpenNewsletter,
  onOpenList,
}: NewsletterPreviewPopupProps) {
  const cacheRef = useRef<NewsletterCache>({});
  const [newsletters, setNewsletters] = useState<FixedNewsletter[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const typeLabel = useMemo(() => {
    if (!newsletterType) {
      return '';
    }

    return NEWSLETTER_TYPE_LABELS[newsletterType] ?? newsletterType;
  }, [newsletterType]);

  useEffect(() => {
    if (!isOpen || !newsletterType || !jwt || !company?.id) {
      return;
    }

    if (cacheRef.current[newsletterType]) {
      setNewsletters(cacheRef.current[newsletterType] ?? []);
      setError(null);
      return;
    }

    const controller = new AbortController();
    let isActive = true;

    const fetchNewslettersByType = async() => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await fetch(`/api/companies/${company.id}/fixed-newsletters`, {
          ...JWTHeader(jwt),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        const parsed = Array.isArray(data) ? data : [];
        const filtered = parsed.filter((item: FixedNewsletter) => item?.type === newsletterType);
        const sorted = filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

        if (!isActive) {
          return;
        }

        cacheRef.current[newsletterType] = sorted;
        setNewsletters(sorted);
      } catch (fetchError) {
        if (!isActive || controller.signal.aborted) {
          return;
        }
        console.error('Unable to load newsletters for preview:', fetchError);
        setError('Impossible de récupérer les newsletters pour ce thème.');
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    fetchNewslettersByType();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [isOpen, newsletterType, jwt]);

  const latestNewsletter = useMemo(() => {
    if (!newsletters.length) {
      return null;
    }

    const sorted = [...newsletters].sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return dateB - dateA;
    });

    return sorted[0] ?? null;
  }, [newsletters]);

  if (!isOpen || !newsletterType) {
    return null;
  }

  const handleOpenNewsletter = (newsletter: FixedNewsletter) => {
    const templateName = getPreferredTemplate(newsletter, company);
    if (!templateName) {
      return;
    }

    onOpenNewsletter(newsletter, templateName);
  };

  const renderContent = () => {
    if (!jwt || !company) {
      return (
        <div className="flex flex-col items-center justify-center gap-4 py-6 text-center text-gray-600">
          <span className="text-4xl" role="img" aria-label="verrou">🔒</span>
          <p>
            Vous devez être authentifié et disposer d&apos;une entreprise pour consulter les newsletters disponibles.
          </p>
        </div>
      );
    }

    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center gap-4 py-10 text-center text-gray-500">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-rgpd-blue" />
          <p>Chargement des newsletters {typeLabel.toLowerCase()}...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center gap-4 py-10 text-center">
          <span className="text-4xl" role="img" aria-label="erreur">⚠️</span>
          <p className="text-gray-600">{error}</p>
          <button
            type="button"
            className="rounded-lg bg-rgpd-blue px-5 py-2 text-white transition-colors hover:bg-rgpd-blue-dark"
            onClick={onOpenList}
          >
            Ouvrir la liste complète des newsletters
          </button>
        </div>
      );
    }

    if (!newsletters.length) {
      return (
        <div className="flex flex-col items-center justify-center gap-4 py-10 text-center text-gray-600">
          <span className="text-4xl" role="img" aria-label="information">ℹ️</span>
          <p>
            Aucune newsletter n&apos;a encore été publiée pour ce thème.
          </p>
          <button
            type="button"
            className="rounded-lg bg-rgpd-blue px-5 py-2 text-white transition-colors hover:bg-rgpd-blue-dark"
            onClick={onOpenList}
          >
            Consulter toutes les newsletters
          </button>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-6">
        <div className="grid gap-6">
          {latestNewsletter && (
            (() => {
              const templateName = getPreferredTemplate(latestNewsletter, company);

              if (!templateName) {
                return (
                  <div className="flex flex-col items-center justify-center gap-4 rounded-lg border border-gray-200 p-6 text-center shadow-sm">
                    <p className="text-sm text-gray-600">
                      Aucun template disponible pour la newsletter la plus récente.
                    </p>
                    <button
                      type="button"
                      className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
                      onClick={onOpenList}
                    >
                      Accéder à toutes les newsletters
                    </button>
                  </div>
                );
              }

              return (
                <div className="flex flex-col gap-4 rounded-lg border border-gray-200 p-4 shadow-sm">
                  <MiniNewsletterPreview
                    date={latestNewsletter.date}
                    slug={latestNewsletter.slug}
                    templateName={templateName}
                    jwt={jwt}
                    company={company}
                    sizeMode="adaptive"
                  />

                  <div className="flex flex-col gap-1 text-center">
                    <h4 className="text-base font-semibold text-gray-800">{latestNewsletter.title}</h4>
                    <p className="text-xs tracking-wide text-gray-500 uppercase">
                      Publiée le {latestNewsletter.availableDate ? formatDate(latestNewsletter.availableDate) : formatDate(latestNewsletter.date)}
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center justify-center gap-3">
                    <button
                      type="button"
                      className="rounded-lg bg-rgpd-blue px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-rgpd-blue-dark"
                      onClick={() => handleOpenNewsletter(latestNewsletter)}
                    >
                      Ouvrir la newsletter
                    </button>
                    <button
                      type="button"
                      className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100"
                      onClick={onOpenList}
                    >
                      Accéder à toutes les newsletters
                    </button>
                  </div>
                </div>
              );
            })()
          )}
        </div>
      </div>
    );
  };

  return (
    <Popup
      isOpen={isOpen}
      onClose={onClose}
      title={`Newsletters ${themeLabel}`}
      maxWidth="960px"
      additionalClass="w-full max-w-5xl"
    >
      {renderContent()}
    </Popup>
  );
}

export default NewsletterPreviewPopup;

